import {Icon, TextSemiBold} from '@hm/fundamental'
import {mdiClose, mdiPlus, mdiRestore} from '@mdi/js'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ColorButton} from './ColorButton'
import {ColorPreview} from './ColorPreview'
import {Tooltip} from '../Tooltip'

import type {HsvColor, MaterialColorSelectorProps} from '@refra/modules/common/types'
import {hexToHsv, hsvToHex, calculatePopoverPosition} from '@refra/modules/common/utils'

export const MaterialColorSelector: React.FC<MaterialColorSelectorProps> = (props) => {
  const {
    refractoryId,
    globalColor,
    localColor: effectiveLocalColor,
    isDisabled = false,
    showResetButton = true,
    hideAddButtonWhenLocalColorSet = false,
    showGlobalColorAsFallback = false,
    showSelectedOutline = true,
    showGlobalColor = false,
    fullWidth = false,
    showTitles = false,
    onColorChange,
    onColorReset,
    dataCy = 'material-color-selector'
  } = props
  const {t} = useTranslation()
  const popoverRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const saturationRef = useRef<HTMLDivElement>(null)
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [hsv, setHsv] = useState<HsvColor>({h: 0, s: 100, v: 100})
  const [isDragging, setIsDragging] = useState(false)
  const [colorFormat] = useState<'HEX'>('HEX')
  const pendingColorRef = useRef<string | null>(null)
  const hsvRef = useRef<HsvColor>(hsv)
  const [popoverPosition, setPopoverPosition] = useState<{top?: string; bottom?: string; left?: string; right?: string}>({
    top: '100%',
    left: '0'
  })

  const [optimisticColor, setOptimisticColor] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    hsvRef.current = hsv
  }, [hsv])

  useEffect(() => {
    if (effectiveLocalColor !== undefined) {
      setOptimisticColor(null)
    }
  }, [effectiveLocalColor])

  const currentHexColor = useMemo(() => hsvToHex(hsv.h, hsv.s, hsv.v), [hsv])

  const saveColor = useCallback(async () => {
    if (pendingColorRef.current && onColorChange) {
      const colorToSave = pendingColorRef.current
      const previousColor = effectiveLocalColor
      setOptimisticColor(colorToSave)
      setError(null)
      pendingColorRef.current = null
      
      try {
        await onColorChange(Number(refractoryId), colorToSave)
      } catch (err) {
        setOptimisticColor(previousColor || null)
        setError(t('localColors.saveError'))
      }
    }
  }, [refractoryId, onColorChange, effectiveLocalColor, t])

  const handleColorChange = useCallback((color: string) => {
    pendingColorRef.current = color
  }, [])

  const handleReset = useCallback(async () => {
    if (onColorReset) {
      const previousColor = effectiveLocalColor
      setOptimisticColor(null)
      setError(null)
      
      try {
        await onColorReset(Number(refractoryId))
        setHsv(hexToHsv(globalColor))
        pendingColorRef.current = null
      } catch (err) {
        setOptimisticColor(previousColor || null)
        setError(t('localColors.resetError'))
      }
    }
  }, [refractoryId, onColorReset, globalColor, effectiveLocalColor, t])

  const handleSelectLocal = useCallback(() => {
    if (!isDisabled && onColorChange) {
      const colorToUse = effectiveLocalColor || globalColor
      setHsv(hexToHsv(colorToUse))
      setPopoverPosition(calculatePopoverPosition(containerRef))
      setIsPickerOpen(true)
    }
  }, [isDisabled, onColorChange, effectiveLocalColor, globalColor])

  const handleOpenPicker = useCallback(() => {
    if (!isDisabled && onColorChange) {
      const effectiveColor = effectiveLocalColor || globalColor
      setHsv(hexToHsv(effectiveColor))
      setPopoverPosition(calculatePopoverPosition(containerRef))
      setIsPickerOpen(true)
    }
  }, [isDisabled, onColorChange, effectiveLocalColor, globalColor])

  const handleClosePicker = useCallback(() => {
    saveColor()
    setIsPickerOpen(false)
  }, [saveColor])

  const handleSaturationMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      setIsDragging(true)
      const rect = saturationRef.current?.getBoundingClientRect()
      if (rect) {
        const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width))
        const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height))
        const newS = (x / rect.width) * 100
        const newV = 100 - (y / rect.height) * 100
        // Use ref to get current hue value
        const newHsv = {h: hsvRef.current.h, s: newS, v: newV}
        setHsv(newHsv)
        handleColorChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v))
      }
    },
    [handleColorChange]
  )

  const handleHueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newH = parseFloat(event.target.value)
      const newHsv = {h: newH, s: hsvRef.current.s, v: hsvRef.current.v}
      setHsv(newHsv)
      handleColorChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v))
    },
    [handleColorChange]
  )

  const handleHexInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value.toUpperCase()
      if (value && !value.startsWith('#')) {
        value = '#' + value
      }

      if (/^#[0-9A-F]{6}$/i.test(value)) {
        setHsv(hexToHsv(value))
        handleColorChange(value)
        saveColor()
      }
    },
    [handleColorChange, saveColor]
  )

  const handleHueMouseUp = useCallback(() => {
    saveColor()
  }, [saveColor])

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging || !saturationRef.current) return
      const rect = saturationRef.current.getBoundingClientRect()
      const x = Math.max(0, Math.min(event.clientX - rect.left, rect.width))
      const y = Math.max(0, Math.min(event.clientY - rect.top, rect.height))
      const newS = (x / rect.width) * 100
      const newV = 100 - (y / rect.height) * 100
      const newHsv = {h: hsvRef.current.h, s: newS, v: newV}
      setHsv(newHsv)
      handleColorChange(hsvToHex(newHsv.h, newHsv.s, newHsv.v))
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      saveColor()
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleColorChange, saveColor])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        handleClosePicker()
      }
    }

    if (isPickerOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isPickerOpen, handleClosePicker])

  const hasLocalOverride = effectiveLocalColor !== undefined || optimisticColor !== null
  const localColor = effectiveLocalColor ?? optimisticColor ?? globalColor
  const displayColor = isPickerOpen ? currentHexColor : localColor

  // If no color change handler is provided, render as read-only
  if (!onColorChange) {
    return <ColorPreview color={effectiveLocalColor || globalColor} />
  }

  return (
    <div className="flex flex-col gap-2" data-cy={dataCy}>
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-cy={`${dataCy}-error`}>
          {error}
        </div>
      )}
      <div className={`flex items-start gap-2 ${fullWidth ? 'w-full' : ''}`}>
        {/* Global Color Section - takes 50% width when fullWidth, removed from DOM when no global color shown */}
        {((showGlobalColorAsFallback && !hasLocalOverride) || showGlobalColor) && (
          <div className={`flex flex-col ${fullWidth ? 'flex-1' : ''}`}>
            {showTitles && (
              <span className="text-xs text-gray-500 mb-1" data-cy={`${dataCy}-global-title`}>
                {t('localColors.globalColor')}
              </span>
            )}
            <div className="flex items-center">
              {/* Catalog mode: show global fallback when no local color */}
              {showGlobalColorAsFallback && !hasLocalOverride && (
                showTitles ? (
                  <ColorButton
                    color={globalColor}
                    isSelected={false}
                    isDisabled={isDisabled}
                    onClick={handleOpenPicker}
                    dataCy={`${dataCy}-global-fallback-btn`}
                  />
                ) : (
                  <Tooltip text={t('localColors.globalColor')}>
                    <ColorButton
                      color={globalColor}
                      isSelected={false}
                      isDisabled={isDisabled}
                      onClick={handleOpenPicker}
                      dataCy={`${dataCy}-global-fallback-btn`}
                    />
                  </Tooltip>
                )
              )}

              {/* Installation modal mode: show global color (read-only) */}
              {showGlobalColor && (
                showTitles ? (
                  <ColorButton
                    color={globalColor}
                    isSelected={false}
                    isDisabled={true}
                    onClick={() => {}}
                    dataCy={`${dataCy}-global-btn`}
                  />
                ) : (
                  <Tooltip text={t('localColors.globalColor')}>
                    <ColorButton
                      color={globalColor}
                      isSelected={false}
                      isDisabled={true}
                      onClick={() => {}}
                      dataCy={`${dataCy}-global-btn`}
                    />
                  </Tooltip>
                )
              )}
            </div>
          </div>
        )}

        {/* Local Color Button and Reset - takes 50% width when fullWidth, content starts from left */}
        <div className={`flex flex-col ${fullWidth ? 'flex-1' : ''}`}>
          {showTitles && (
            <span className="text-xs text-gray-500 mb-1" data-cy={`${dataCy}-local-title`}>
              {t('localColors.localColor')}
            </span>
          )}
          <div className="flex items-center gap-2">
            {/* Local Color Button (Editable) - shown when not using global fallback or when local color exists */}
            {(!showGlobalColorAsFallback || hasLocalOverride) && (
              showTitles ? (
                <ColorButton
                  color={displayColor}
                  isSelected={showSelectedOutline}
                  isDisabled={isDisabled}
                  onClick={handleOpenPicker}
                  dataCy={`${dataCy}-local-btn`}
                />
              ) : (
                <Tooltip text={hasLocalOverride ? t('localColors.localColor') : t('localColors.setLocalColor')}>
                  <ColorButton
                    color={displayColor}
                    isSelected={showSelectedOutline}
                    isDisabled={isDisabled}
                    onClick={handleOpenPicker}
                    dataCy={`${dataCy}-local-btn`}
                  />
                </Tooltip>
              )
            )}
          <div className="relative" ref={containerRef}>
          {isPickerOpen && (
            <div
              ref={popoverRef}
              className="absolute z-50 bg-white border border-border rounded-lg shadow-lg w-72"
              style={{
                marginTop: popoverPosition.top === '100%' ? '8px' : undefined,
                marginBottom: popoverPosition.bottom === '100%' ? '8px' : undefined,
                ...popoverPosition
              }}
              data-cy={`${dataCy}-picker-popover`}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <TextSemiBold>{t('localColors.customColor')}</TextSemiBold>
                <button
                  type="button"
                  onClick={handleClosePicker}
                  className="p-1 rounded hover:bg-background-hover transition-colors"
                  data-cy={`${dataCy}-picker-close`}
                >
                  <Icon path={mdiClose} customSize={1} />
                </button>
              </div>
              <div className="p-4 flex flex-col gap-3">
                <div
                  ref={saturationRef}
                  className="relative w-full h-20 rounded cursor-crosshair"
                  style={{
                    background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h}, 100%, 50%))`
                  }}
                  onMouseDown={handleSaturationMouseDown}
                  data-cy={`${dataCy}-saturation-picker`}
                >
                  <div
                    className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md transform -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                    style={{
                      left: `${hsv.s}%`,
                      top: `${100 - hsv.v}%`,
                      backgroundColor: currentHexColor
                    }}
                    data-cy={`${dataCy}-saturation-indicator`}
                  />
                </div>

                {/* Hue slider */}
                <div className="relative">
                  <style>
                    {`
                      .hue-slider::-webkit-slider-thumb {
                        -webkit-appearance: none;
                        appearance: none;
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 3px solid black;
                        cursor: pointer;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                      }
                      .hue-slider::-moz-range-thumb {
                        width: 24px;
                        height: 24px;
                        border-radius: 50%;
                        background: white;
                        border: 3px solid black;
                        cursor: pointer;
                        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
                      }
                    `}
                  </style>
                  <input
                    type="range"
                    min="0"
                    max="360"
                    value={hsv.h}
                    onChange={handleHueChange}
                    onMouseUp={handleHueMouseUp}
                    onTouchEnd={handleHueMouseUp}
                    className="hue-slider w-full h-6 rounded-lg appearance-none cursor-pointer"
                    style={{
                      background:
                        'linear-gradient(to right, #ff0000, #ffff00, #00ff00, #00ffff, #0000ff, #ff00ff, #ff0000)'
                    }}
                    data-cy={`${dataCy}-hue-slider`}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 px-4 py-3 border-t border-border">
                <select
                  value={colorFormat}
                  disabled
                  className="px-2 py-1.5 border border-border rounded bg-background text-sm"
                  data-cy={`${dataCy}-format-select`}
                >
                  <option value="HEX">HEX</option>
                </select>
                <input
                  type="text"
                  value={currentHexColor}
                  onChange={handleHexInputChange}
                  placeholder="#000000"
                  maxLength={7}
                  className="flex-1 px-2 py-1.5 border border-border rounded bg-background text-sm font-mono uppercase"
                  data-cy={`${dataCy}-hex-input`}
                />
              </div>
            </div>
          )}
        </div>

          {showResetButton && hasLocalOverride && (
            <Tooltip text={t('localColors.resetToGlobal')}>
              <button
                type="button"
                onClick={handleReset}
                disabled={isDisabled}
                className={`
                  inline-flex items-center justify-center w-8 h-8 rounded border-2 border-border
                  cursor-pointer transition-all hover:border-primary hover:bg-background-hover
                  disabled:opacity-50 disabled:cursor-default disabled:hover:border-border disabled:hover:bg-transparent
                `}
                data-cy={`${dataCy}-reset-btn`}
              >
                <Icon path={mdiRestore} customSize={0.6} />
              </button>
            </Tooltip>
          )}
          </div>
        </div>
      </div>

      {showResetButton && <span className="text-[10px] text-gray-400">{t('localColors.appliedLocallyOnly')}</span>}
    </div>
  )
}
